/**
 * PaymentHeader — Reads payment data from Redux and displays metadata.
 */
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { useAppSelector } from 'src/store/hooks';
import { CopyableText } from 'src/components/common/CopyableText';
import { formatIsoTimestamp, formatLatency } from 'src/utils/formatters';

const stateToIndicatorType = (state: string) => {
  if (state === 'SUCCESS') return 'success' as const;
  if (state === 'FAILED') return 'error' as const;
  return 'in-progress' as const;
};

export const PaymentHeader = () => {
  const payment = useAppSelector(state => state.payment.data);
  if (!payment) return null;

  return (
    <Container
      header={
        <Header variant="h2">
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <CopyableText value={payment.id} />
            <StatusIndicator type={stateToIndicatorType(payment.beaconPaymentState)}>
              {payment.beaconPaymentState}
            </StatusIndicator>
          </SpaceBetween>
        </Header>
      }
    >
      <ColumnLayout columns={5} variant="text-grid">
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Current Status</Box>
          <Box>{payment.beaconPaymentStatus}</Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Payment Type</Box>
          <Box>{payment.paymentType ? <Badge color="blue">{payment.paymentType}</Badge> : '—'}</Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Plugin</Box>
          <Box>{payment.pluginName ? <Badge>{payment.pluginName}</Badge> : '—'}</Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Total Latency</Box>
          <Box>{formatLatency(payment.totalLatencyMs)}</Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Created</Box>
          <Box>{formatIsoTimestamp(payment.createdAt)}</Box>
        </SpaceBetween>
      </ColumnLayout>
    </Container>
  );
};
